/**
 * Cache TTL (Time-To-Live) configurations in milliseconds.
 *
 * Guidelines:
 * - Data that rarely changes: 15-30 minutes
 * - Data that changes occasionally: 5 minutes
 * - Volatile data (stats, metrics): 2 minutes
 * - Session-scoped data (auth, user): Infinity
 */
export const CACHE_TTL = {
  /** Reference data (integrations, questionnaires) - 30 minutes */
  REFERENCE_DATA: 30 * 60 * 1000,

  /** Merchant data - 5 minutes (changes occasionally) */
  MERCHANTS: 5 * 60 * 1000,

  /** Location data - 5 minutes (merchant-scoped, changes occasionally) */
  LOCATIONS: 5 * 60 * 1000,

  /** Configuration (settings, preferences) - 15 minutes */
  CONFIGURATION: 15 * 60 * 1000,

  /** Stats/metrics - 2 minutes (volatile, frequently updated) */
  STATISTICS: 2 * 60 * 1000,

  /** Session-scoped data (auth, permissions) - never expires */
  SESSION: Infinity,

  /**
   * Finished eval executions - never expires.
   *
   * A summary.json and a case result are written once when the run finishes and never change,
   * so re-fetching one within a session can only return the same bytes.
   */
  EVALUATION_RESULTS: Infinity,

  /**
   * An engineer's own tickets, PRs and actions - 30 seconds.
   *
   * The page is for acting on work that moves while it is open, so a revisit refetches quickly;
   * the fleet keeps its own short cache behind it.
   */
  MY_WORK: 30 * 1000,

  /**
   * Claude on my PRs, the engineer's own level and the staff list - 1 minute.
   *
   * It changes only when someone saves it, and every save here invalidates it.
   */
  CLAUDE_ON_MY_PRS: 60 * 1000,

  /**
   * How Claude reaches the person, their quiet hours and browsers - 1 minute.
   *
   * It changes only when they save it here, and each save's answer replaces it.
   */
  FLEET_NOTIFICATIONS: 60 * 1000,

  /**
   * How the person's PR heads-ups post: like FLEET_NOTIFICATIONS, it changes when they save it here, or connect Slack.
   */
  FLEET_HEADS_UP: 60 * 1000,

  /**
   * The person's own runner limits (My runner) - 1 minute.
   *
   * It changes when they save it here or from one of their runner machines; a save here invalidates it.
   */
  MY_RUNNER: 60 * 1000,

  /**
   * Every engineer's own Claude settings (Team settings) - 1 minute.
   *
   * Each person changes their own elsewhere, so a minute is as fresh as the page needs.
   */
  TEAM_SETTINGS: 60 * 1000,

  /**
   * The TE fix versions to pick from - 10 minutes.
   *
   * Versions are created a release at a time; the fleet keeps them ten minutes too.
   */
  RELEASE_LIST: 10 * 60 * 1000,

  /**
   * One release's tickets, QA and blockers - 60 seconds.
   *
   * The fleet shares one read of a release for five minutes; this only saves a revisit from asking.
   */
  RELEASE_PAGE: 60 * 1000,

  /**
   * The QA queue - 60 seconds.
   *
   * The fleet shares one read of the queue for two minutes; this only saves a revisit from asking.
   */
  QA_QUEUE: 60 * 1000,

  /**
   * What Claude is doing on the rows a page shows - 15 seconds.
   *
   * The fleet shares its runs and requests reads for 15 seconds; the page polls every 30 while it is open.
   */
  IN_FLIGHT: 15 * 1000,

  /**
   * Everything Claude did on one ticket - 30 seconds.
   *
   * Opened from a row to check on work that may still be moving.
   */
  TICKET_TIMELINE: 30 * 1000,

  /**
   * Search results, the ticket page, costs and runner contribution - 30 seconds.
   *
   * Typed and revisited in quick succession; the fleet reads Jira and the run records each time.
   */
  CLAUDE_HUB: 30 * 1000,

  /**
   * The team lists (decisions needed, approved, merged, cancelled) - 30 seconds.
   *
   * They change as people answer cards in Slack, so a revisit should not show a card already answered.
   */
  CLAUDE_TEAM: 30 * 1000,

  /**
   * Quality snapshots, delivery metrics and MCP usage - 5 minutes.
   *
   * Written each morning (snapshots) or cached by the fleet for 10 minutes (usage); a revisit need not ask again.
   */
  CLAUDE_QUALITY: 5 * 60 * 1000,

  /**
   * The Claude tools catalog - 5 minutes.
   *
   * Rebuilt when a tool or a deploy changes, at most hourly; the fleet holds the deploy status a minute.
   */
  CLAUDE_TOOLS: 5 * 60 * 1000,

  /**
   * The Claude Wishlist - 1 minute.
   *
   * Votes and status changes come from everyone on staff; a minute keeps the counts close without a read per render.
   */
  CLAUDE_WISHLIST: 60 * 1000,

  /**
   * Whether Talk it over is on, and the kinds it takes - 5 minutes.
   *
   * An operator turns it on or off in the fleet settings; a page need not ask on every render.
   */
  TALK_STATUS: 5 * 60 * 1000
} as const

/**
 * Cache key type definitions for type safety and autocomplete.
 *
 * Each key is a readonly tuple where:
 * - First element: resource type (string literal)
 * - Remaining elements: scoping identifiers (IDs, filter objects)
 *
 * ADX keys are NOT always merchant-scoped (unlike merchant-dashboard).
 * Some are global (all merchants), some are merchant-scoped, some are entity-scoped.
 */

export type MerchantCacheKey =
  | readonly ['merchants', 'all']           // All merchants (full collection)
  | readonly ['merchants', 'active']        // Active merchants only (default load)
  // Same collections with test merchants excluded server-side. A separate key, because the two
  // differ in CONTENT, not just in presentation - toggling the setting must refetch rather than
  // re-filter a cached superset that never contained the rows.
  | readonly ['merchants', 'all', 'no-test']
  | readonly ['merchants', 'active', 'no-test']
  | readonly ['merchants', 'recent-active'] // Recently created active merchants
  | readonly ['merchants', number]          // Single merchant by ID

export type LocationCacheKey =
  | readonly ['locations', number]                // All locations for a merchant
  | readonly ['locations', number, number]        // Single location by merchant + location ID

export type SocialMediaProviderCacheKey =
  | readonly ['social-media-providers']           // All social media providers

export type SnapshotObjectiveCacheKey =
  | readonly ['snapshot-objectives', number]      // A merchant's snapshot objective-eligibility list

// Every key below the feature carries the run scope: two scopes can hold an execution of the same
// name, so a key without it would serve one scope's summary for the other's.
export type EvaluationCacheKey =
  | readonly ['evaluations', 'features']                                   // Features with published results
  | readonly ['evaluations', string, 'scopes']                             // Run scopes a feature has results under
  | readonly ['evaluations', string, string, 'executions', number, string] // A page of a feature + scope's executions
  | readonly ['evaluations', string, string, string]                       // One execution's summary.json
  | readonly ['evaluations', string, string, string, string, string]       // One iteration's full result
  | readonly ['evaluations', string, string, 'history', string]            // A set of summaries, for trends

export type MyWorkCacheKey =
  | readonly ['my-work']              // Everything on the My work page
  | readonly ['my-work', 'overview']  // GET /fleet/me
  | readonly ['my-work', 'counts']    // GET /fleet/me/counts, the side nav badge

export type ClaudeOnMyPrsCacheKey =
  | readonly ['claude-on-my-prs']              // Everything Claude on my PRs caches
  | readonly ['claude-on-my-prs', 'mine']      // GET /fleet/me/claude-on-my-prs
  | readonly ['claude-on-my-prs', 'everyone']  // GET /fleet/claude-on-my-prs

export type FleetNotificationsCacheKey = readonly ['fleet-notifications'] // GET /fleet/me/notifications

export type FleetHeadsUpCacheKey = readonly ['fleet-heads-up'] // GET /fleet/me/heads-up

export type MyRunnerCacheKey =
  | readonly ['my-runner']          // Everything My runner caches
  | readonly ['my-runner', 'mine']  // GET /fleet/me/my-runner

export type TeamSettingsCacheKey = readonly ['team-settings'] // GET /fleet/team-settings and /fleet/runners

export type ReleaseCacheKey =
  | readonly ['releases']                  // Everything on the Releases page
  | readonly ['releases', 'list']          // GET /fleet/releases
  | readonly ['releases', 'page', string]  // GET /fleet/releases/<name>

export type QaQueueCacheKey =
  | readonly ['qa-queue']                                 // Everything on the QA queue
  | readonly ['qa-queue', string, string, boolean]        // GET /fleet/qa-queue?version=&mobile=&mine=

export type InFlightCacheKey =
  | readonly ['in-flight']           // Everything any page asked about work in flight
  | readonly ['in-flight', string]   // GET /fleet/in-flight?targets=<sorted, comma-separated>

export type TicketTimelineCacheKey =
  | readonly ['ticket-timeline', string]   // GET /fleet/timeline/<key>

export type ClaudeHubCacheKey =
  | readonly ['claude-hub']                                   // Everything the Claude pages share
  | readonly ['claude-hub', 'search', string]                 // GET /fleet/search?q=
  | readonly ['claude-hub', 'ticket', string]                 // GET /fleet/tickets/<key>
  | readonly ['claude-hub', 'costs', string, number]          // GET /fleet/costs?by=&days=
  | readonly ['claude-hub', 'contribution', string]           // GET /fleet/runners/contribution?month=

export type ClaudeTeamCacheKey =
  | readonly ['claude-team']                                  // Every team list
  | readonly ['claude-team', string]                          // GET /fleet/team/<list>

export type ClaudeQualityCacheKey =
  | readonly ['claude-quality']                               // everything the Tests page reads
  | readonly ['claude-quality', 'nightly']                    // GET /fleet/quality/nightly
  | readonly ['claude-quality', 'quarantine']                 // GET /fleet/quality/quarantine
  | readonly ['claude-quality', 'escapes', number]            // GET /fleet/quality/escapes?days=
  | readonly ['claude-quality', 'sweep']                      // GET /fleet/quality/sweep

export type TalkItOverCacheKey =
  | readonly ['talk-it-over', 'status']                       // GET /fleet/talk

export type ClaudeMetricsCacheKey =
  | readonly ['claude-metrics', 'delivery', string, string, string]   // GET /fleet/metrics/delivery?from=&to=&engineer=
  | readonly ['claude-metrics', 'tickets', string]           // GET /fleet/metrics/delivery/tickets?<slice>
  | readonly ['claude-metrics', 'mcp-usage', number]          // GET /fleet/mcp/usage?days=
  | readonly ['claude-metrics', 'outcomes']                    // GET /fleet/metrics/outcomes
  | readonly ['claude-metrics', 'period', string, string]     // GET /fleet/metrics/period?kind=&key=

export type ClaudeToolsCacheKey =
  | readonly ['claude-tools']                                 // GET /fleet/claude-tools

export type ClaudeWishlistCacheKey =
  | readonly ['claude-wishlist']                              // every wishlist read
  | readonly ['claude-wishlist', 'list', string, string, number, number]   // GET /fleet/wishes?filter=&q=&page=&pageSize=
  | readonly ['claude-wishlist', 'one', string]               // GET /fleet/wishes/<id>

/**
 * Union of all cache key types for strict type checking.
 * Add new cache key types to this union as services are migrated.
 *
 * IMPORTANT: Do NOT add a generic fallback (e.g. readonly (string | number)[]).
 * Every resource must have an explicit key type for type safety.
 */
export type CacheKey =
  | MerchantCacheKey
  | LocationCacheKey
  | SocialMediaProviderCacheKey
  | SnapshotObjectiveCacheKey
  | EvaluationCacheKey
  | MyWorkCacheKey
  | ClaudeOnMyPrsCacheKey
  | FleetNotificationsCacheKey
  | FleetHeadsUpCacheKey
  | MyRunnerCacheKey
  | TeamSettingsCacheKey
  | ReleaseCacheKey
  | QaQueueCacheKey
  | InFlightCacheKey
  | TicketTimelineCacheKey
  | ClaudeHubCacheKey
  | ClaudeTeamCacheKey
  | ClaudeQualityCacheKey
  | ClaudeMetricsCacheKey
  | ClaudeToolsCacheKey
  | ClaudeWishlistCacheKey
  | TalkItOverCacheKey

/**
 * Cache key factories for consistent, type-safe hierarchical keys.
 *
 * PATTERN RULES:
 * 1. First element: Resource type (e.g., 'merchants', 'locations')
 * 2. Remaining elements: Scoping identifiers (IDs, filter objects)
 * 3. Keys are hierarchical — invalidating a parent invalidates all children
 *
 * INVALIDATION HIERARCHY:
 * - ['merchants'] → Invalidates ALL merchant data
 * - ['merchants', merchantId] → Invalidates ONE specific merchant
 * - ['locations', merchantId] → Invalidates ALL locations for a merchant
 * - ['locations', merchantId, locationId] → Invalidates ONE specific location
 *
 * Add key factories here as services are migrated.
 *
 * @example
 * CACHE_KEYS.merchants.all()           // ['merchants', 'all']
 * CACHE_KEYS.merchants.recentActive()  // ['merchants', 'recent-active']
 * CACHE_KEYS.merchants.byId(123)       // ['merchants', 123]
 */
export const CACHE_KEYS = {
  /**
   * Merchant cache keys.
   */
  merchants: {
    /**
     * All merchants (full collection fetched across all pages).
     *
     * @returns Cache key
     *
     * @example
     * CACHE_KEYS.merchants.all()
     * // Returns: ['merchants', 'all']
     */
    all: (excludeTest = false): MerchantCacheKey =>
      excludeTest ? (['merchants', 'all', 'no-test'] as const) : (['merchants', 'all'] as const),

    /**
     * Active merchants only (default load — most consumers need this).
     *
     * @returns Cache key
     *
     * @example
     * CACHE_KEYS.merchants.active()
     * // Returns: ['merchants', 'active']
     */
    active: (excludeTest = false): MerchantCacheKey =>
      excludeTest ? (['merchants', 'active', 'no-test'] as const) : (['merchants', 'active'] as const),

    /**
     * Recently created active merchants (lightweight endpoint).
     *
     * @returns Cache key
     *
     * @example
     * CACHE_KEYS.merchants.recentActive()
     * // Returns: ['merchants', 'recent-active']
     */
    recentActive: (): MerchantCacheKey => ['merchants', 'recent-active'] as const,

    /**
     * Single merchant by ID.
     *
     * @param id - Merchant ID
     * @returns Cache key
     *
     * @example
     * CACHE_KEYS.merchants.byId(123)
     * // Returns: ['merchants', 123]
     */
    byId: (id: number): MerchantCacheKey => ['merchants', id] as const
  },

  /**
   * Location cache keys (merchant-scoped).
   */
  locations: {
    /**
     * All locations for a merchant (full collection including inactive).
     *
     * @param merchantId - Merchant ID
     * @returns Cache key
     *
     * @example
     * CACHE_KEYS.locations.byMerchant(42)
     * // Returns: ['locations', 42]
     */
    byMerchant: (merchantId: number): LocationCacheKey =>
      ['locations', merchantId] as const,

    /**
     * Single location by merchant + location ID.
     *
     * @param merchantId - Merchant ID
     * @param locationId - Location ID
     * @returns Cache key
     *
     * @example
     * CACHE_KEYS.locations.byId(42, 101)
     * // Returns: ['locations', 42, 101]
     */
    byId: (merchantId: number, locationId: number): LocationCacheKey =>
      ['locations', merchantId, locationId] as const
  },

  /**
   * Social media provider cache keys.
   */
  socialMediaProviders: {
    /**
     * All social media providers.
     *
     * @returns Cache key
     *
     * @example
     * CACHE_KEYS.socialMediaProviders.all()
     * // Returns: ['social-media-providers']
     */
    all: (): SocialMediaProviderCacheKey => ['social-media-providers'] as const
  },

  /**
   * Snapshot objective-eligibility cache keys (merchant-scoped).
   */
  snapshotObjectives: {
    /**
     * A merchant's snapshots with their objective-eligibility state.
     *
     * @param merchantId - Merchant ID
     * @returns Cache key
     *
     * @example
     * CACHE_KEYS.snapshotObjectives.byMerchant(42)
     * // Returns: ['snapshot-objectives', 42]
     */
    byMerchant: (merchantId: number): SnapshotObjectiveCacheKey =>
      ['snapshot-objectives', merchantId] as const
  },

  /**
   * LLM evaluation cache keys (feature- and execution-scoped).
   *
   * Summaries and case results are immutable once an execution has finished, so they are
   * cached for the whole session — see CACHE_TTL.EVALUATION_RESULTS.
   *
   * Everything below the feature is also scoped by the run scope (`env=dev`, `pr=5160`, …). The
   * same execution name can exist in two scopes, so the scope is part of the key rather than a
   * detail of the query.
   */
  evaluations: {
    /**
     * Features that have published eval results.
     *
     * @example
     * CACHE_KEYS.evaluations.features()
     * // Returns: ['evaluations', 'features']
     */
    features: (): EvaluationCacheKey => ['evaluations', 'features'] as const,

    /**
     * The run scopes one feature has results under.
     *
     * @param feature - Feature name, e.g. 'response-tagging'
     *
     * @example
     * CACHE_KEYS.evaluations.scopes('response-tagging')
     * // Returns: ['evaluations', 'response-tagging', 'scopes']
     */
    scopes: (feature: string): EvaluationCacheKey =>
      ['evaluations', feature, 'scopes'] as const,

    /**
     * The most recent executions of one feature within one run scope.
     *
     * @param feature - Feature name, e.g. 'response-tagging'
     * @param scope - Run scope, e.g. 'env=dev'
     * @param limit - How many executions were requested (part of the key: a wider list is a different query)
     * @param before - Paging cursor, `''` for the first page — a different page is a different query
     *
     * @example
     * CACHE_KEYS.evaluations.executions('response-tagging', 'env=dev', 20, '')
     * // Returns: ['evaluations', 'response-tagging', 'env=dev', 'executions', 20, '']
     */
    executions: (
      feature: string,
      scope: string,
      limit: number,
      before: string
    ): EvaluationCacheKey =>
      ['evaluations', feature, scope, 'executions', limit, before] as const,

    /**
     * One execution's summary.json.
     *
     * @example
     * CACHE_KEYS.evaluations.summary('response-tagging', 'env=dev', '20260825T163346')
     * // Returns: ['evaluations', 'response-tagging', 'env=dev', '20260825T163346']
     */
    summary: (feature: string, scope: string, executionName: string): EvaluationCacheKey =>
      ['evaluations', feature, scope, executionName] as const,

    /**
     * One iteration's full result.
     *
     * @example
     * CACHE_KEYS.evaluations.case('response-tagging', 'env=dev', '20260825T163346', 'golden-50', 'CONF-03')
     * // Returns: ['evaluations', 'response-tagging', 'env=dev', '20260825T163346', 'golden-50', 'CONF-03']
     */
    case: (
      feature: string,
      scope: string,
      executionName: string,
      scenarioName: string,
      iterationName: string
    ): EvaluationCacheKey =>
      ['evaluations', feature, scope, executionName, scenarioName, iterationName] as const,

    /**
     * A set of summaries loaded together for the History trend.
     *
     * @param feature - Feature name
     * @param scope - Run scope the set belongs to — a trend never crosses scopes
     * @param executionNames - The executions in the set, joined — a different set is a different query
     *
     * @example
     * CACHE_KEYS.evaluations.history('response-tagging', 'env=dev', ['20260825T163346', '20260825T155910'])
     * // Returns: ['evaluations', 'response-tagging', 'env=dev', 'history', '20260825T163346,20260825T155910']
     */
    history: (
      feature: string,
      scope: string,
      executionNames: string[]
    ): EvaluationCacheKey =>
      ['evaluations', feature, scope, 'history', executionNames.join(',')] as const
  },

  /**
   * My work cache keys (the signed-in engineer's own work; the fleet knows who from the token).
   */
  myWork: {
    /**
     * Everything the My work page caches - what an action or an answered decision invalidates.
     *
     * @example
     * CACHE_KEYS.myWork.all()
     * // Returns: ['my-work']
     */
    all: (): MyWorkCacheKey => ['my-work'] as const,

    /**
     * The person's tickets, PRs, decisions and recent actions.
     *
     * @example
     * CACHE_KEYS.myWork.overview()
     * // Returns: ['my-work', 'overview']
     */
    overview: (): MyWorkCacheKey => ['my-work', 'overview'] as const,

    /**
     * The side nav badge: PRs ready to merge and decisions waiting.
     *
     * @example
     * CACHE_KEYS.myWork.counts()
     * // Returns: ['my-work', 'counts']
     */
    counts: (): MyWorkCacheKey => ['my-work', 'counts'] as const
  },

  /**
   * Claude on my PRs cache keys (the per-engineer review and fix level).
   */
  claudeOnMyPrs: {
    /**
     * Everything Claude on my PRs caches - what any save invalidates.
     *
     * @example
     * CACHE_KEYS.claudeOnMyPrs.all()
     * // Returns: ['claude-on-my-prs']
     */
    all: (): ClaudeOnMyPrsCacheKey => ['claude-on-my-prs'] as const,

    /**
     * The signed-in engineer's own level.
     *
     * @example
     * CACHE_KEYS.claudeOnMyPrs.mine()
     * // Returns: ['claude-on-my-prs', 'mine']
     */
    mine: (): ClaudeOnMyPrsCacheKey => ['claude-on-my-prs', 'mine'] as const,

    /**
     * Every engineer's level, for the fleet settings.
     *
     * @example
     * CACHE_KEYS.claudeOnMyPrs.everyone()
     * // Returns: ['claude-on-my-prs', 'everyone']
     */
    everyone: (): ClaudeOnMyPrsCacheKey => ['claude-on-my-prs', 'everyone'] as const
  },

  /**
   * How Claude reaches the engineer for each kind of thing, their quiet hours, and their browsers.
   *
   * @example
   * CACHE_KEYS.fleetNotifications.mine()
   * // Returns: ['fleet-notifications']
   */
  fleetNotifications: {
    mine: (): FleetNotificationsCacheKey => ['fleet-notifications'] as const
  },

  /**
   * How the engineer's own PR heads-ups post, and whether their Slack account is connected.
   *
   * @example
   * CACHE_KEYS.fleetHeadsUp.mine()
   * // Returns: ['fleet-heads-up']
   */
  fleetHeadsUp: {
    mine: (): FleetHeadsUpCacheKey => ['fleet-heads-up'] as const
  },

  /**
   * The person's own runner limits: daily share, reserve, daily cap and opt-outs.
   *
   * @example
   * CACHE_KEYS.myRunner.mine()
   * // Returns: ['my-runner', 'mine']
   */
  myRunner: {
    all: (): MyRunnerCacheKey => ['my-runner'] as const,
    mine: (): MyRunnerCacheKey => ['my-runner', 'mine'] as const
  },

  /**
   * Every engineer's own Claude settings, with their runner machines.
   *
   * @example
   * CACHE_KEYS.teamSettings.all()
   * // Returns: ['team-settings']
   */
  teamSettings: {
    all: (): TeamSettingsCacheKey => ['team-settings'] as const
  },

  releases: {
    /**
     * Everything the Releases page caches.
     *
     * @example
     * CACHE_KEYS.releases.all()
     * // Returns: ['releases']
     */
    all: (): ReleaseCacheKey => ['releases'] as const,

    /**
     * The TE fix versions to pick from.
     *
     * @example
     * CACHE_KEYS.releases.list()
     * // Returns: ['releases', 'list']
     */
    list: (): ReleaseCacheKey => ['releases', 'list'] as const,

    /**
     * One release's page, by fix version name.
     *
     * @example
     * CACHE_KEYS.releases.page('13.21.0')
     * // Returns: ['releases', 'page', '13.21.0']
     */
    page: (name: string): ReleaseCacheKey => ['releases', 'page', name] as const
  },

  qaQueue: {
    /**
     * Everything the QA queue caches.
     *
     * @example
     * CACHE_KEYS.qaQueue.all()
     * // Returns: ['qa-queue']
     */
    all: (): QaQueueCacheKey => ['qa-queue'] as const,

    /**
     * The queue for a version, a mobile version ("" for the fleet's current ones) and whether it is mine only.
     *
     * @example
     * CACHE_KEYS.qaQueue.page('13.21.0', '', false)
     * // Returns: ['qa-queue', '13.21.0', '', false]
     */
    page: (version: string, mobile: string, mine: boolean): QaQueueCacheKey =>
      ['qa-queue', version, mobile, mine] as const
  },

  inFlight: {
    /**
     * Every page's read of work in flight.
     *
     * @example
     * CACHE_KEYS.inFlight.all()
     * // Returns: ['in-flight']
     */
    all: (): InFlightCacheKey => ['in-flight'] as const,

    /**
     * One page's targets, sorted so the same rows share one read.
     *
     * @example
     * CACHE_KEYS.inFlight.targets(['adx#887', 'TE-5'])
     * // Returns: ['in-flight', 'TE-5,adx#887']
     */
    targets: (targets: string[]): InFlightCacheKey => ['in-flight', [...targets].sort().join(',')] as const
  },

  ticketTimeline: {
    /**
     * Everything Claude did on one ticket.
     *
     * @example
     * CACHE_KEYS.ticketTimeline.byKey('TE-123')
     * // Returns: ['ticket-timeline', 'TE-123']
     */
    byKey: (key: string): TicketTimelineCacheKey => ['ticket-timeline', key] as const
  },

  claudeHub: {
    /**
     * Everything the Claude pages share.
     *
     * @example
     * CACHE_KEYS.claudeHub.all()
     * // Returns: ['claude-hub']
     */
    all: (): ClaudeHubCacheKey => ['claude-hub'] as const,

    /**
     * One search box query, as typed (trimmed).
     *
     * @example
     * CACHE_KEYS.claudeHub.search('TE-123')
     * // Returns: ['claude-hub', 'search', 'TE-123']
     */
    search: (q: string): ClaudeHubCacheKey => ['claude-hub', 'search', q] as const,

    /**
     * One ticket's page.
     *
     * @example
     * CACHE_KEYS.claudeHub.ticket('TE-123')
     * // Returns: ['claude-hub', 'ticket', 'TE-123']
     */
    ticket: (key: string): ClaudeHubCacheKey => ['claude-hub', 'ticket', key] as const,

    /**
     * The most expensive tickets or PRs over some days.
     *
     * @example
     * CACHE_KEYS.claudeHub.costs('ticket', 30)
     * // Returns: ['claude-hub', 'costs', 'ticket', 30]
     */
    costs: (by: string, days: number): ClaudeHubCacheKey => ['claude-hub', 'costs', by, days] as const,

    /**
     * Each laptop runner's month ("" for the current one).
     *
     * @example
     * CACHE_KEYS.claudeHub.contribution('2026-10')
     * // Returns: ['claude-hub', 'contribution', '2026-10']
     */
    contribution: (month: string): ClaudeHubCacheKey => ['claude-hub', 'contribution', month] as const
  },

  claudeTeam: {
    /**
     * Every team list, so an answered card re-reads them all.
     *
     * @example
     * CACHE_KEYS.claudeTeam.all()
     * // Returns: ['claude-team']
     */
    all: (): ClaudeTeamCacheKey => ['claude-team'] as const,

    /**
     * One team list: decisions, approved, merged or cancelled.
     *
     * @example
     * CACHE_KEYS.claudeTeam.list('decisions')
     * // Returns: ['claude-team', 'decisions']
     */
    list: (name: string): ClaudeTeamCacheKey => ['claude-team', name] as const
  },

  talkItOver: {
    /**
     * Whether Talk it over is on for the signed-in person, and the kinds it takes.
     *
     * @example
     * CACHE_KEYS.talkItOver.status()
     * // Returns: ['talk-it-over', 'status']
     */
    status: (): TalkItOverCacheKey => ['talk-it-over', 'status'] as const
  },

  claudeQuality: {
    /**
     * Everything the Tests page reads, so a row button's change reads it all again.
     *
     * @example
     * CACHE_KEYS.claudeQuality.all()
     * // Returns: ['claude-quality']
     */
    all: (): ClaudeQualityCacheKey => ['claude-quality'] as const,

    /**
     * The last nightly Playwright runs.
     *
     * @example
     * CACHE_KEYS.claudeQuality.nightly()
     * // Returns: ['claude-quality', 'nightly']
     */
    nightly: (): ClaudeQualityCacheKey => ['claude-quality', 'nightly'] as const,

    /**
     * The quarantine markers in tattle-playwright.
     *
     * @example
     * CACHE_KEYS.claudeQuality.quarantine()
     * // Returns: ['claude-quality', 'quarantine']
     */
    quarantine: (): ClaudeQualityCacheKey => ['claude-quality', 'quarantine'] as const,

    /**
     * Escapes over some days.
     *
     * @example
     * CACHE_KEYS.claudeQuality.escapes(90)
     * // Returns: ['claude-quality', 'escapes', 90]
     */
    escapes: (days: number): ClaudeQualityCacheKey => ['claude-quality', 'escapes', days] as const,

    /**
     * The daily suite sweep's latest result, with each open question's answer.
     *
     * @example
     * CACHE_KEYS.claudeQuality.sweep()
     * // Returns: ['claude-quality', 'sweep']
     */
    sweep: (): ClaudeQualityCacheKey => ['claude-quality', 'sweep'] as const
  },

  claudeMetrics: {
    /**
     * Delivery metrics for a period (New York days) and engineer ("" for all).
     *
     * @example
     * CACHE_KEYS.claudeMetrics.delivery('2026-10-19', '2026-10-25', '')
     * // Returns: ['claude-metrics', 'delivery', '2026-10-19', '2026-10-25', '']
     */
    delivery: (from: string, to: string, engineer: string): ClaudeMetricsCacheKey =>
      ['claude-metrics', 'delivery', from, to, engineer] as const,

    /**
     * One page of a slice of the delivery tickets, keyed by its query string.
     *
     * @example
     * CACHE_KEYS.claudeMetrics.tickets('from=2026-10-19&pr=merged&page=1')
     * // Returns: ['claude-metrics', 'tickets', 'from=2026-10-19&pr=merged&page=1']
     */
    tickets: (query: string): ClaudeMetricsCacheKey => ['claude-metrics', 'tickets', query] as const,

    /**
     * The fleet digest of one day, week or month ("" for the newest of that kind).
     *
     * @example
     * CACHE_KEYS.claudeMetrics.period('week', '2026-10-19')
     * // Returns: ['claude-metrics', 'period', 'week', '2026-10-19']
     */
    period: (kind: string, key: string): ClaudeMetricsCacheKey => ['claude-metrics', 'period', kind, key] as const,

    /**
     * MCP connector usage over some days.
     *
     * @example
     * CACHE_KEYS.claudeMetrics.mcpUsage(30)
     * // Returns: ['claude-metrics', 'mcp-usage', 30]
     */
    mcpUsage: (days: number): ClaudeMetricsCacheKey => ['claude-metrics', 'mcp-usage', days] as const,

    /**
     * Cost per useful outcome and the model-tuning evidence.
     *
     * @example
     * CACHE_KEYS.claudeMetrics.outcomes()
     * // Returns: ['claude-metrics', 'outcomes']
     */
    outcomes: (): ClaudeMetricsCacheKey => ['claude-metrics', 'outcomes'] as const
  },

  claudeTools: {
    /**
     * The Claude tools catalog and what is deployed.
     *
     * @example
     * CACHE_KEYS.claudeTools.catalog()
     * // Returns: ['claude-tools']
     */
    catalog: (): ClaudeToolsCacheKey => ['claude-tools'] as const
  },

  claudeWishlist: {
    /**
     * Every wishlist read, for invalidation after a write.
     *
     * @example
     * CACHE_KEYS.claudeWishlist.all()
     * // Returns: ['claude-wishlist']
     */
    all: (): ClaudeWishlistCacheKey => ['claude-wishlist'] as const,

    /**
     * One page of one filter and search.
     *
     * @example
     * CACHE_KEYS.claudeWishlist.list({ filter: 'open' })
     * // Returns: ['claude-wishlist', 'list', 'open', '', 1, 20]
     */
    list: (params: { filter: string; q?: string; page?: number; pageSize?: number }): ClaudeWishlistCacheKey =>
      ['claude-wishlist', 'list', params.filter, params.q ?? '', params.page ?? 1, params.pageSize ?? 20] as const,

    /**
     * One wish, opened from a link to it.
     *
     * @example
     * CACHE_KEYS.claudeWishlist.one('abcdef012345')
     * // Returns: ['claude-wishlist', 'one', 'abcdef012345']
     */
    one: (id: string): ClaudeWishlistCacheKey => ['claude-wishlist', 'one', id] as const
  }
} as const
